import { ContextMenuItem, TrashIcon } from '@tunein/react-ui';
import type {
  ChildGuideActions,
  ChildGuideItem,
  ResolvedMenuActionItem,
} from '@tunein/web-api-client';
import { useContext } from 'react';
import { removeRecent } from 'src/common/actions/browse';
import { showMessage } from 'src/common/actions/messagePopover';
import {
  CLEAR_RECENTS_ERROR,
  CLEAR_SINGLE_RECENT_BUTTON,
} from 'src/common/constants/localizations/settings';
import useActions from 'src/common/hooks/useActions';
import { LocationAndLocalizationContext } from '../../../../providers/LocationAndLocalizationProvider';

interface RemoveRecentActions {
  removeRecent: (guideId: string) => Promise<unknown>;
  showMessage: (message: string) => void;
}

export interface GuideItemMenuItemProps {
  guideItem: ChildGuideItem;
  menuAction: ResolvedMenuActionItem;
}

/**
 * Removes a single item from recents. Only rendered when the API lists `RemoveRecent` among the
 * item's `menuActions` (see GuideItemLongPressMenu).
 */
export function RemoveRecentMenuItem({ guideItem, menuAction }: GuideItemMenuItemProps) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const actions = useActions<RemoveRecentActions>({ removeRecent, showMessage });

  const action = menuAction.action as ChildGuideActions['removeRecent'];
  const guideId = action?.guideId || guideItem.guideId;

  if (!guideId) {
    return null;
  }

  const handleClick = async () => {
    try {
      await actions.removeRecent(guideId);
    } catch {
      actions.showMessage(CLEAR_RECENTS_ERROR);
    }
  };

  return (
    <ContextMenuItem data-testid="removeRecentMenuItem" icon={<TrashIcon />} onClick={handleClick}>
      {getLocalizedText(CLEAR_SINGLE_RECENT_BUTTON)}
    </ContextMenuItem>
  );
}
